// PDF + Excel exporters for the MDM report.
// PDF uses Carlito (metric-compatible with Calibri) and a formal document layout
// with class-wise color bands so each category is easy to identify.

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx-js-style";
import { CATEGORY_DISPLAY, isValidServiceDate } from "./mdmData";
import { calculateReport } from "./mdmCalculator";

// -------- Carlito font loader (cached across exports) --------
let _fontCache = null;
async function loadCarlito(doc) {
  if (!_fontCache) {
    const [reg, bold, italic] = await Promise.all([
      fetch("/fonts/Carlito-Regular.ttf").then((r) => r.arrayBuffer()),
      fetch("/fonts/Carlito-Bold.ttf").then((r) => r.arrayBuffer()),
      fetch("/fonts/Carlito-Italic.ttf").then((r) => r.arrayBuffer()),
    ]);
    const toB64 = (buf) => {
      let s = "";
      const bytes = new Uint8Array(buf);
      const CHUNK = 0x8000;
      for (let i = 0; i < bytes.length; i += CHUNK) {
        s += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
      }
      return btoa(s);
    };
    _fontCache = { reg: toB64(reg), bold: toB64(bold), italic: toB64(italic) };
  }
  doc.addFileToVFS("Carlito-Regular.ttf", _fontCache.reg);
  doc.addFileToVFS("Carlito-Bold.ttf", _fontCache.bold);
  doc.addFileToVFS("Carlito-Italic.ttf", _fontCache.italic);
  doc.addFont("Carlito-Regular.ttf", "Carlito", "normal");
  doc.addFont("Carlito-Bold.ttf", "Carlito", "bold");
  doc.addFont("Carlito-Italic.ttf", "Carlito", "italic");
  doc.setFont("Carlito", "normal");
}

// ------------------------------------------------------------------
// Formal PDF export
// ------------------------------------------------------------------
export async function exportPDF(result) {
  const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
  await loadCarlito(doc);

  const primary = [4, 120, 87];         // brand green
  const accent = [234, 88, 12];         // saffron
  const inkDark = [15, 23, 42];
  const inkMuted = [71, 85, 105];
  const line = [203, 213, 225];
  const softBg = [248, 250, 252];

  const dateStr = result.date.toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });
  const refNo = `PPC/${result.academic_year}/${String(result.date.getTime()).slice(-6)}`;
  const nowStr = new Date().toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  const school = result.school || {};
  const schoolName = (school.name || "").trim();
  const udise = (school.udise || "").trim();

  // ------ Letterhead ------
  // Coloured top band
  doc.setFillColor(...primary);
  doc.rect(0, 0, 210, 8, "F");
  doc.setFillColor(...accent);
  doc.rect(0, 8, 210, 1.5, "F");

  // Emblem placeholder (rounded square with initials)
  doc.setFillColor(...primary);
  doc.roundedRect(14, 14, 18, 18, 3, 3, "F");
  doc.setFont("Carlito", "bold");
  doc.setFontSize(13);
  doc.setTextColor(255, 255, 255);
  doc.text("PMC", 23, 25.5, { align: "center" });

  // Title block
  doc.setTextColor(...inkDark);
  doc.setFont("Carlito", "bold");
  doc.setFontSize(15);
  doc.text(schoolName ? schoolName.toUpperCase() : "PM POSHAN MID-DAY MEAL PROGRAMME", 37, 20, { maxWidth: 130 });
  doc.setFont("Carlito", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(...inkMuted);
  if (schoolName) {
    doc.text(`UDISE Code: ${udise || "—"}   ·   PM POSHAN Mid-Day Meal Programme`, 37, 25.5);
  } else {
    doc.text("Daily Cooking-Cost & Quantity Statement", 37, 25.5);
  }
  doc.text("Daily Cooking-Cost & Quantity Statement", 37, 30);

  // Right-side reference block
  doc.setFont("Carlito", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...inkMuted);
  const rightX = 196;
  doc.text(`Ref. No.: ${refNo}`, rightX, 18, { align: "right" });
  doc.text(`Dated: ${dateStr}`, rightX, 22.5, { align: "right" });
  doc.text(`Academic year: ${result.academic_year}`, rightX, 27, { align: "right" });

  // Divider
  doc.setDrawColor(...line);
  doc.setLineWidth(0.3);
  doc.line(14, 36, 196, 36);

  // ------ Subject line ------
  let y = 44;
  doc.setFont("Carlito", "bold");
  doc.setFontSize(10.5);
  doc.setTextColor(...inkDark);
  doc.text("Subject:", 14, y);
  doc.setFont("Carlito", "normal");
  doc.text(
    `Statement of raw materials consumed and cooking cost for the mid-day meal served on ${dateStr} (${result.weekday_name}).`,
    32, y, { maxWidth: 164 }
  );

  y = 60;
  doc.setFont("Carlito", "bold");
  doc.setFontSize(10.5);
  doc.text("Menu of the day:", 14, y);
  doc.setFont("Carlito", "normal");
  doc.text(stripEmoji(result.menu_display), 46, y);

  // ------ Summary strip ------
  y = 68;
  const cards = [
    { l: "Students Served",    v: String(result.summary.total_students) },
    { l: "Cooking Cost (₹)",   v: money(result.summary.grand_total) },
    { l: "Cost per Student",   v: money(result.summary.avg_per_student) },
    { l: "Rice Requirement",   v: `${result.summary.total_rice_kg.toFixed(2)} kg` },
  ];
  cards.forEach((c, i) => {
    const x = 14 + i * 45.5;
    doc.setFillColor(...softBg);
    doc.setDrawColor(...line);
    doc.roundedRect(x, y, 42.5, 18, 2, 2, "FD");
    doc.setFont("Carlito", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...inkMuted);
    doc.text(c.l.toUpperCase(), x + 3, y + 5.5);
    doc.setFont("Carlito", "bold");
    doc.setFontSize(12.5);
    doc.setTextColor(...inkDark);
    doc.text(c.v, x + 3, y + 13.5);
  });

  // ------ Aggregated Ingredients ------
  y = y + 25;
  sectionHeading(doc, "1. Aggregated Ingredients Consumed (all categories)", 14, y, primary);
  const aggRows = Object.entries(result.aggregated_items)
    .sort((a, b) => b[1].total_cost - a[1].total_cost)
    .map(([n, d]) => [
      stripEmoji(n),
      qtyLabel(d.total_quantity, d.unit),
      money(d.avg_rate),
      money(d.total_cost),
    ]);
  autoTable(doc, {
    head: [["Ingredient", "Total Quantity", "Avg Rate (₹)", "Total Cost (₹)"]],
    body: aggRows,
    foot: [["Grand Total", "—", "—", money(result.summary.grand_total)]],
    startY: y + 4,
    theme: "grid",
    styles: { font: "Carlito", fontSize: 9.5, cellPadding: 2, textColor: inkDark, lineColor: line, lineWidth: 0.15 },
    headStyles: { fillColor: primary, textColor: 255, fontStyle: "bold" },
    footStyles: { fillColor: [236, 253, 245], textColor: primary, fontStyle: "bold" },
    columnStyles: { 1: { halign: "center" }, 2: { halign: "right" }, 3: { halign: "right" } },
    alternateRowStyles: { fillColor: [252, 252, 252] },
    margin: { left: 14, right: 14 },
  });

  // ------ Class-wise sections (color banded) ------
  let sectionNum = 2;
  for (const [key, cat] of Object.entries(result.categories)) {
    if (!cat.student_count) continue;
    const meta = CATEGORY_DISPLAY[key];
    const colr = meta.pdf;

    let cursorY = doc.lastAutoTable.finalY + 6;
    if (cursorY > 245) { doc.addPage(); cursorY = 20; }

    // Coloured left-band section title
    doc.setFillColor(...colr);
    doc.rect(14, cursorY - 4.5, 3.5, 8, "F");
    doc.setFont("Carlito", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...inkDark);
    doc.text(`${sectionNum}. ${meta.full}`, 20, cursorY + 1);
    doc.setFont("Carlito", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...inkMuted);
    doc.text(
      `Students: ${cat.student_count}   ·   Subtotal: ${money(cat.subtotal)}   ·   Per student: ${money(cat.per_student_total)}`,
      20, cursorY + 6
    );

    const rows = Object.entries(cat.items)
      .sort((a, b) => b[1].total_cost - a[1].total_cost)
      .map(([n, it]) => [
        stripEmoji(n) + (it.is_auto_balanced ? "  (balanced)" : ""),
        `${it.qty_per_student} ${it.unit}`,
        money(it.rate_per_student),
        qtyLabel(it.total_quantity, it.unit),
        money(it.total_cost),
      ]);
    autoTable(doc, {
      head: [["Item", "Per Student", "Rate (₹)", "Total Qty", "Amount (₹)"]],
      body: rows,
      foot: [["Total Amount", "", "", "", money(cat.subtotal)]],
      startY: cursorY + 8,
      theme: "grid",
      styles: { font: "Carlito", fontSize: 8.5, cellPadding: 1.5, textColor: inkDark, lineColor: line, lineWidth: 0.15 },
      headStyles: { fillColor: colr, textColor: 255, fontStyle: "bold" },
      footStyles: { fillColor: mix(colr, 0.9), textColor: colr, fontStyle: "bold" },
      columnStyles: { 1: { halign: "center" }, 2: { halign: "right" }, 3: { halign: "center" }, 4: { halign: "right" } },
      alternateRowStyles: { fillColor: [252, 252, 252] },
      margin: { left: 14, right: 14 },
    });
    sectionNum++;
  }

  // ------ Certification / Signatures (fit on the current page whenever possible) ------
  // Use a compact 28mm signature block so it fits on the same page as the last table.
  const SIGNATURE_BLOCK_HEIGHT = 28;
  const FOOTER_TOP_Y = 282;
  let cursorY = doc.lastAutoTable.finalY + 4;

  // If the block would collide with the footer, push to a new page.
  if (cursorY + SIGNATURE_BLOCK_HEIGHT > FOOTER_TOP_Y) {
    doc.addPage();
    cursorY = 30;
  }

  // Compact certification line (one line, small font)
  doc.setFont("Carlito", "italic");
  doc.setFontSize(8.5);
  doc.setTextColor(...inkMuted);
  doc.text(
    "Certified that the quantities and cost reported above have been calculated as per the prevailing per-child norms and rates.",
    14, cursorY + 2, { maxWidth: 182 }
  );

  // Signature row directly below certification line
  const sigY = cursorY + 15;
  doc.setDrawColor(...inkMuted);
  doc.setLineWidth(0.3);
  const labels = ["Meal In-charge", "Head Master / Principal", "Inspecting In-charge"];
  labels.forEach((l, i) => {
    const px = 18 + i * 62;
    doc.line(px, sigY, px + 50, sigY);
    doc.setFont("Carlito", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...inkDark);
    doc.text(l, px + 25, sigY + 4, { align: "center" });
    doc.setFont("Carlito", "italic");
    doc.setFontSize(7);
    doc.setTextColor(...inkMuted);
    doc.text("(Signature & date)", px + 25, sigY + 8, { align: "center" });
  });

  // ------ Footer with page numbers + contact info ------
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    // Footer line
    doc.setDrawColor(...line);
    doc.setLineWidth(0.2);
    doc.line(14, 286, 196, 286);
    doc.setFont("Carlito", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...inkMuted);
    doc.text(`Generated ${nowStr}  ·  Generated by PM Poshan MDM Calculator`, 14, 292);
    doc.text(`Page ${i} of ${totalPages}`, 196, 292, { align: "right" });
    // Bottom accent stripe
    doc.setFillColor(...accent);
    doc.rect(0, 295, 210, 1.5, "F");
    doc.setFillColor(...primary);
    doc.rect(0, 296.5, 210, 0.5, "F");
  }

  const filename = `PM_POSHAN_${result.date.toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
}

// ------------------------------------------------------------------
// Excel export (unchanged in style)
// ------------------------------------------------------------------
export function exportExcel(result) {
  const wb = XLSX.utils.book_new();
  const dateStr = result.date.toLocaleDateString("en-IN");

  const summaryRows = [
    ["PM POSHAN Daily Report"],
    [`Date: ${dateStr} (${result.weekday_name})`],
    [`Menu: ${result.menu_display}`],
    [`Academic year: ${result.academic_year}`],
    [],
    ["Metric", "Value"],
    ["Total Students", result.summary.total_students],
    ["Grand Total (₹)", result.summary.grand_total],
    ["Avg / Student (₹)", result.summary.avg_per_student],
    ["Total Rice (kg)", result.summary.total_rice_kg],
  ];
  const ws1 = XLSX.utils.aoa_to_sheet(summaryRows);
  ws1["!cols"] = [{ wch: 24 }, { wch: 18 }];
  XLSX.utils.book_append_sheet(wb, ws1, "Summary");

  const aggRows = [["Item", "Total Qty", "Unit", "Avg Rate (₹)", "Total Cost (₹)"]];
  for (const [n, d] of Object.entries(result.aggregated_items)) {
    aggRows.push([stripEmoji(n), d.total_quantity, d.unit, d.avg_rate, d.total_cost]);
  }
  const ws2 = XLSX.utils.aoa_to_sheet(aggRows);
  ws2["!cols"] = [{ wch: 22 }, { wch: 12 }, { wch: 8 }, { wch: 14 }, { wch: 16 }];
  XLSX.utils.book_append_sheet(wb, ws2, "Ingredients");

  for (const [key, cat] of Object.entries(result.categories)) {
    if (!cat.student_count) continue;
    const rows = [
      [CATEGORY_DISPLAY[key].full],
      [`Students: ${cat.student_count}`, `Subtotal: ₹${cat.subtotal.toFixed(2)}`, `Per student: ₹${cat.per_student_total.toFixed(2)}`],
      [],
      ["Item", "Qty/Student", "Unit", "Rate/Student (₹)", "Total Qty", "Total Cost (₹)", "Notes"],
      ...Object.entries(cat.items).map(([n, d]) => [
        stripEmoji(n), d.qty_per_student, d.unit, d.rate_per_student, d.total_quantity, d.total_cost, d.is_auto_balanced ? "Auto-balanced" : "",
      ]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 20 }, { wch: 12 }, { wch: 8 }, { wch: 16 }, { wch: 12 }, { wch: 14 }, { wch: 18 }];
    XLSX.utils.book_append_sheet(wb, ws, CATEGORY_DISPLAY[key].name.slice(0, 30));
  }

  const filename = `PM_POSHAN_${result.date.toISOString().slice(0, 10)}.xlsx`;
  XLSX.writeFile(wb, filename);
}

// ------------------------------------------------------------------
// helpers
// ------------------------------------------------------------------
function money(v) { return Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function qtyLabel(q, unit) {
  if (unit === "g" && q >= 1000) return `${(q / 1000).toFixed(2)} kg`;
  return `${q} ${unit}`;
}
function stripEmoji(s) {
  // Remove emoji + variation selectors so PDF renders text-only cleanly
  return String(s || "").replace(
    /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{1F000}-\u{1F2FF}\uFE0F]/gu,
    ""
  ).replace(/\s+/g, " ").trim();
}
function mix(rgb, alpha) {
  // simple mix with white
  return [
    Math.round(rgb[0] + (255 - rgb[0]) * alpha),
    Math.round(rgb[1] + (255 - rgb[1]) * alpha),
    Math.round(rgb[2] + (255 - rgb[2]) * alpha),
  ];
}
function sectionHeading(doc, text, x, y, color) {
  doc.setFillColor(...color);
  doc.rect(x, y - 4.5, 3.5, 6.5, "F");
  doc.setFont("Carlito", "bold");
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text(text, x + 6, y + 0.5);
}

// ------------------------------------------------------------------
// BULK PDF — one page per day + summary sheet
// ------------------------------------------------------------------

export async function exportBulkPDF({ fromDate, toDate, counts, overrides, school }) {
  const days = [];
  const cur = new Date(fromDate);
  cur.setHours(0, 0, 0, 0);
  const end = new Date(toDate);
  end.setHours(23, 59, 59, 999);
  while (cur <= end) {
    if (isValidServiceDate(cur)) days.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  if (!days.length) throw new Error("No valid dates in the selected range");

  // Pre-compute per-day report
  const reports = days.map((d) => {
    const r = calculateReport(d, counts, overrides);
    r.school = school;
    return r;
  });

  const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
  await loadCarlito(doc);

  const primary = [4, 120, 87];
  const accent = [234, 88, 12];
  const inkDark = [15, 23, 42];
  const inkMuted = [71, 85, 105];
  const line = [203, 213, 225];
  const softBg = [248, 250, 252];

  const nowStr = new Date().toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  const schoolName = (school?.name || "").trim();
  const udise = (school?.udise || "").trim();
  const fromStr = fromDate.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  const toStr = toDate.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

  // -------- Summary sheet --------
  drawLetterhead(doc, {
    title: schoolName ? schoolName.toUpperCase() : "PM POSHAN BULK REPORT",
    sub1: schoolName ? `UDISE Code: ${udise || "—"}   ·   PM POSHAN Bulk Cooking-Cost Report` : "Bulk Cooking-Cost & Quantity Statement",
    sub2: `Consolidated report for ${days.length} day(s)`,
    refNo: `PPC/BULK/${fromDate.getTime().toString().slice(-6)}`,
    dated: `${fromStr} → ${toStr}`,
    ayear: `Days: ${days.length}`,
    primary, accent, inkDark, inkMuted, line,
  });

  // Aggregate across all days
  const grandTotals = { grand_total: 0, total_students: 0, total_rice_kg: 0 };
  const perCat = { balvatika: { c: 0, s: 0 }, primary: { c: 0, s: 0 }, upper_primary: { c: 0, s: 0 }, high_school: { c: 0, s: 0 } };
  const perItem = {};
  for (const r of reports) {
    grandTotals.grand_total += r.summary.grand_total;
    grandTotals.total_students += r.summary.total_students;
    grandTotals.total_rice_kg += r.summary.total_rice_kg;
    for (const [k, c] of Object.entries(r.categories)) {
      perCat[k].c += c.student_count;
      perCat[k].s += c.subtotal;
    }
    for (const [n, d] of Object.entries(r.aggregated_items)) {
      if (!perItem[n]) perItem[n] = { total_quantity: 0, total_cost: 0, unit: d.unit, emoji: d.emoji };
      perItem[n].total_quantity += d.total_quantity;
      perItem[n].total_cost += d.total_cost;
    }
  }

  // KPI strip
  let y = 68;
  const cards = [
    { l: "Days Reported", v: String(days.length) },
    { l: "Total Meals", v: String(grandTotals.total_students) },
    { l: "Total Cost (₹)", v: money(grandTotals.grand_total) },
    { l: "Total Rice", v: `${grandTotals.total_rice_kg.toFixed(2)} kg` },
  ];
  cards.forEach((c, i) => {
    const x = 14 + i * 45.5;
    doc.setFillColor(...softBg); doc.setDrawColor(...line);
    doc.roundedRect(x, y, 42.5, 18, 2, 2, "FD");
    doc.setFont("Carlito", "normal"); doc.setFontSize(7.5); doc.setTextColor(...inkMuted);
    doc.text(c.l.toUpperCase(), x + 3, y + 5.5);
    doc.setFont("Carlito", "bold"); doc.setFontSize(12.5); doc.setTextColor(...inkDark);
    doc.text(c.v, x + 3, y + 13.5);
  });

  // Per-day table
  y = y + 25;
  sectionHeading(doc, "1. Day-wise Summary", 14, y, primary);
  autoTable(doc, {
    head: [["Date", "Day", "Students", "Rice (kg)", "Total Cost (₹)"]],
    body: reports.map((r) => [
      r.date.toLocaleDateString("en-IN"),
      r.weekday_name,
      String(r.summary.total_students),
      r.summary.total_rice_kg.toFixed(2),
      money(r.summary.grand_total),
    ]),
    foot: [["Grand Total", "", String(grandTotals.total_students), grandTotals.total_rice_kg.toFixed(2), money(grandTotals.grand_total)]],
    startY: y + 4,
    theme: "grid",
    styles: { font: "Carlito", fontSize: 9, cellPadding: 1.6, textColor: inkDark, lineColor: line, lineWidth: 0.15 },
    headStyles: { fillColor: primary, textColor: 255, fontStyle: "bold" },
    footStyles: { fillColor: [236, 253, 245], textColor: primary, fontStyle: "bold" },
    columnStyles: { 2: { halign: "center" }, 3: { halign: "right" }, 4: { halign: "right" } },
    alternateRowStyles: { fillColor: [252, 252, 252] },
    margin: { left: 14, right: 14 },
  });

  // Category-wise summary
  let cursorY = doc.lastAutoTable.finalY + 8;
  if (cursorY > 240) { doc.addPage(); cursorY = 30; }
  sectionHeading(doc, "2. Category-wise Totals", 14, cursorY, accent);
  const catRows = Object.entries(perCat).map(([k, v]) => [
    CATEGORY_DISPLAY[k].name, String(v.c), money(v.s),
  ]);
  autoTable(doc, {
    head: [["Category", "Total Meals", "Total Cost (₹)"]],
    body: catRows,
    foot: [["Total", String(grandTotals.total_students), money(grandTotals.grand_total)]],
    startY: cursorY + 4,
    theme: "grid",
    styles: { font: "Carlito", fontSize: 9, cellPadding: 1.8, textColor: inkDark, lineColor: line, lineWidth: 0.15 },
    headStyles: { fillColor: accent, textColor: 255, fontStyle: "bold" },
    footStyles: { fillColor: [255, 247, 237], textColor: accent, fontStyle: "bold" },
    columnStyles: { 1: { halign: "center" }, 2: { halign: "right" } },
    margin: { left: 14, right: 14 },
  });

  // Aggregated ingredients across the whole range
  cursorY = doc.lastAutoTable.finalY + 8;
  if (cursorY > 240) { doc.addPage(); cursorY = 30; }
  sectionHeading(doc, "3. Consolidated Ingredients (all days, all categories)", 14, cursorY, primary);
  const aggRows = Object.entries(perItem)
    .sort((a, b) => b[1].total_cost - a[1].total_cost)
    .map(([n, d]) => [stripEmoji(n), qtyLabel(d.total_quantity, d.unit), money(d.total_cost)]);
  autoTable(doc, {
    head: [["Ingredient", "Total Quantity", "Total Cost (₹)"]],
    body: aggRows,
    foot: [["Grand Total", "—", money(grandTotals.grand_total)]],
    startY: cursorY + 4,
    theme: "grid",
    styles: { font: "Carlito", fontSize: 9, cellPadding: 1.6, textColor: inkDark, lineColor: line, lineWidth: 0.15 },
    headStyles: { fillColor: primary, textColor: 255, fontStyle: "bold" },
    footStyles: { fillColor: [236, 253, 245], textColor: primary, fontStyle: "bold" },
    columnStyles: { 1: { halign: "center" }, 2: { halign: "right" } },
    alternateRowStyles: { fillColor: [252, 252, 252] },
    margin: { left: 14, right: 14 },
  });

  // -------- Per-day pages (compact) --------
  for (const r of reports) {
    doc.addPage();
    drawLetterhead(doc, {
      title: schoolName ? schoolName.toUpperCase() : "PM POSHAN DAILY REPORT",
      sub1: schoolName ? `UDISE Code: ${udise || "—"}   ·   PM POSHAN Mid-Day Meal Programme` : "Daily Cooking-Cost & Quantity Statement",
      sub2: `Menu: ${stripEmoji(r.menu_display)}`,
      refNo: `PPC/${r.academic_year}/${String(r.date.getTime()).slice(-6)}`,
      dated: r.date.toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" }),
      ayear: `${r.weekday_name}   ·   AY ${r.academic_year}`,
      primary, accent, inkDark, inkMuted, line,
    });

    // Compact KPI strip
    let py = 44;
    const kcards = [
      { l: "Students", v: String(r.summary.total_students) },
      { l: "Total Cost (₹)", v: money(r.summary.grand_total) },
      { l: "Per Student", v: money(r.summary.avg_per_student) },
      { l: "Rice", v: `${r.summary.total_rice_kg.toFixed(2)} kg` },
    ];
    kcards.forEach((c, i) => {
      const x = 14 + i * 45.5;
      doc.setFillColor(...softBg); doc.setDrawColor(...line);
      doc.roundedRect(x, py, 42.5, 15, 2, 2, "FD");
      doc.setFont("Carlito", "normal"); doc.setFontSize(7); doc.setTextColor(...inkMuted);
      doc.text(c.l.toUpperCase(), x + 3, py + 5);
      doc.setFont("Carlito", "bold"); doc.setFontSize(11); doc.setTextColor(...inkDark);
      doc.text(c.v, x + 3, py + 12);
    });

    // Aggregated ingredients for the day
    py = py + 22;
    sectionHeading(doc, "Ingredients", 14, py, primary);
    const dayAggRows = Object.entries(r.aggregated_items)
      .sort((a, b) => b[1].total_cost - a[1].total_cost)
      .map(([n, d]) => [
        stripEmoji(n), qtyLabel(d.total_quantity, d.unit), money(d.avg_rate), money(d.total_cost),
      ]);
    autoTable(doc, {
      head: [["Ingredient", "Total Qty", "Avg Rate (₹)", "Total Cost (₹)"]],
      body: dayAggRows,
      foot: [["Grand Total", "—", "—", money(r.summary.grand_total)]],
      startY: py + 4,
      theme: "grid",
      styles: { font: "Carlito", fontSize: 8.5, cellPadding: 1.4, textColor: inkDark, lineColor: line, lineWidth: 0.15 },
      headStyles: { fillColor: primary, textColor: 255, fontStyle: "bold" },
      footStyles: { fillColor: [236, 253, 245], textColor: primary, fontStyle: "bold" },
      columnStyles: { 1: { halign: "center" }, 2: { halign: "right" }, 3: { halign: "right" } },
      alternateRowStyles: { fillColor: [252, 252, 252] },
      margin: { left: 14, right: 14 },
    });

    // Category-wise compact summary
    let cy = doc.lastAutoTable.finalY + 6;
    sectionHeading(doc, "Category-wise Totals", 14, cy, accent);
    const catBody = Object.entries(r.categories)
      .filter(([, c]) => c.student_count > 0)
      .map(([k, c]) => [CATEGORY_DISPLAY[k].name, String(c.student_count), money(c.per_student_total), money(c.subtotal)]);
    autoTable(doc, {
      head: [["Category", "Students", "Per Student (₹)", "Total Amount (₹)"]],
      body: catBody.length ? catBody : [["No categories with attendance", "—", "—", "—"]],
      startY: cy + 4,
      theme: "grid",
      styles: { font: "Carlito", fontSize: 8.5, cellPadding: 1.4, textColor: inkDark, lineColor: line, lineWidth: 0.15 },
      headStyles: { fillColor: accent, textColor: 255, fontStyle: "bold" },
      columnStyles: { 1: { halign: "center" }, 2: { halign: "right" }, 3: { halign: "right" } },
      alternateRowStyles: { fillColor: [252, 252, 252] },
      margin: { left: 14, right: 14 },
    });
  }

  // Footer with page numbers only (no contact info)
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setDrawColor(...line);
    doc.setLineWidth(0.2);
    doc.line(14, 286, 196, 286);
    doc.setFont("Carlito", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...inkMuted);
    doc.text(`Generated ${nowStr}  ·  Generated by PM Poshan MDM Calculator`, 14, 292);
    doc.text(`Page ${i} of ${totalPages}`, 196, 292, { align: "right" });
    doc.setFillColor(...accent);
    doc.rect(0, 295, 210, 1.5, "F");
    doc.setFillColor(...primary);
    doc.rect(0, 296.5, 210, 0.5, "F");
  }

  const filename = `PM_POSHAN_BULK_${fromDate.toISOString().slice(0, 10)}_to_${toDate.toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
}

// ------------------------------------------------------------------
// COMPACT ONE-PAGER PDF — fits everything on a single A4 page
// ------------------------------------------------------------------
export async function exportCompactPDF(result) {
  const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
  await loadCarlito(doc);

  const primary = [4, 120, 87];
  const accent = [234, 88, 12];
  const inkDark = [15, 23, 42];
  const inkMuted = [71, 85, 105];
  const line = [203, 213, 225];
  const softBg = [248, 250, 252];

  const dateStr = result.date.toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });
  const nowStr = new Date().toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  const school = result.school || {};
  const schoolName = (school.name || "").trim();
  const udise = (school.udise || "").trim();

  drawLetterhead(doc, {
    title: schoolName ? schoolName.toUpperCase() : "PM POSHAN DAILY REPORT",
    sub1: schoolName ? `UDISE Code: ${udise || "—"}   ·   PM POSHAN Mid-Day Meal Programme` : "Daily Cooking-Cost & Quantity Statement",
    sub2: `Menu: ${stripEmoji(result.menu_display)}`,
    refNo: `PPC/${result.academic_year}/${String(result.date.getTime()).slice(-6)}`,
    dated: dateStr,
    ayear: `${result.weekday_name}   ·   AY ${result.academic_year}`,
    primary, accent, inkDark, inkMuted, line,
  });

  // KPI strip (tight)
  let y = 42;
  const cards = [
    { l: "Students", v: String(result.summary.total_students) },
    { l: "Total Cost (₹)", v: money(result.summary.grand_total) },
    { l: "Per Student (₹)", v: money(result.summary.avg_per_student) },
    { l: "Rice", v: `${result.summary.total_rice_kg.toFixed(2)} kg` },
  ];
  cards.forEach((c, i) => {
    const x = 14 + i * 45.5;
    doc.setFillColor(...softBg); doc.setDrawColor(...line);
    doc.roundedRect(x, y, 42.5, 13, 2, 2, "FD");
    doc.setFont("Carlito", "normal"); doc.setFontSize(7); doc.setTextColor(...inkMuted);
    doc.text(c.l.toUpperCase(), x + 3, y + 4.5);
    doc.setFont("Carlito", "bold"); doc.setFontSize(11); doc.setTextColor(...inkDark);
    doc.text(c.v, x + 3, y + 10.5);
  });

  // Aggregated ingredients (compact)
  y = y + 18;
  sectionHeading(doc, "Aggregated Ingredients (all categories)", 14, y, primary);
  const aggRows = Object.entries(result.aggregated_items)
    .sort((a, b) => b[1].total_cost - a[1].total_cost)
    .map(([n, d]) => [stripEmoji(n), qtyLabel(d.total_quantity, d.unit), money(d.avg_rate), money(d.total_cost)]);
  autoTable(doc, {
    head: [["Ingredient", "Total Qty", "Avg Rate (₹)", "Total Cost (₹)"]],
    body: aggRows,
    foot: [["Grand Total", "—", "—", money(result.summary.grand_total)]],
    startY: y + 4,
    theme: "grid",
    styles: { font: "Carlito", fontSize: 8, cellPadding: 1.1, textColor: inkDark, lineColor: line, lineWidth: 0.12 },
    headStyles: { fillColor: primary, textColor: 255, fontStyle: "bold", fontSize: 8 },
    footStyles: { fillColor: [236, 253, 245], textColor: primary, fontStyle: "bold", fontSize: 8 },
    columnStyles: { 1: { halign: "center" }, 2: { halign: "right" }, 3: { halign: "right" } },
    alternateRowStyles: { fillColor: [252, 252, 252] },
    margin: { left: 14, right: 14 },
  });

  // Category-wise inline table (no per-category deep dives — just totals per class)
  let cy = doc.lastAutoTable.finalY + 5;
  sectionHeading(doc, "Category-wise Totals", 14, cy, accent);
  const catRows = Object.entries(result.categories)
    .filter(([, c]) => c.student_count > 0)
    .map(([k, c]) => [CATEGORY_DISPLAY[k].name, String(c.student_count), money(c.per_student_total), money(c.subtotal)]);
  autoTable(doc, {
    head: [["Category", "Students", "Per Student (₹)", "Total (₹)"]],
    body: catRows.length ? catRows : [["No categories with attendance", "—", "—", "—"]],
    foot: [["Total", String(result.summary.total_students), "", money(result.summary.grand_total)]],
    startY: cy + 4,
    theme: "grid",
    styles: { font: "Carlito", fontSize: 8, cellPadding: 1.1, textColor: inkDark, lineColor: line, lineWidth: 0.12 },
    headStyles: { fillColor: accent, textColor: 255, fontStyle: "bold", fontSize: 8 },
    footStyles: { fillColor: [255, 247, 237], textColor: accent, fontStyle: "bold", fontSize: 8 },
    columnStyles: { 1: { halign: "center" }, 2: { halign: "right" }, 3: { halign: "right" } },
    margin: { left: 14, right: 14 },
  });

  // Signature row (tight, one-line labels)
  let sy = doc.lastAutoTable.finalY + 8;
  if (sy > 258) sy = 258; // guarantee it fits on the same page
  doc.setFont("Carlito", "italic"); doc.setFontSize(7.5); doc.setTextColor(...inkMuted);
  doc.text("Certified that the quantities and cost reported above have been calculated as per the prevailing per-child norms and rates.", 14, sy, { maxWidth: 182 });
  const sigY = sy + 12;
  doc.setDrawColor(...inkMuted); doc.setLineWidth(0.3);
  const labels = ["Meal In-charge", "Head Master / Principal", "Inspecting In-charge"];
  labels.forEach((l, i) => {
    const px = 18 + i * 62;
    doc.line(px, sigY, px + 50, sigY);
    doc.setFont("Carlito", "bold"); doc.setFontSize(8); doc.setTextColor(...inkDark);
    doc.text(l, px + 25, sigY + 4, { align: "center" });
    doc.setFont("Carlito", "italic"); doc.setFontSize(6.5); doc.setTextColor(...inkMuted);
    doc.text("(Signature & date)", px + 25, sigY + 7.5, { align: "center" });
  });

  // Footer (no contact info)
  doc.setDrawColor(...line); doc.setLineWidth(0.2);
  doc.line(14, 286, 196, 286);
  doc.setFont("Carlito", "normal"); doc.setFontSize(7); doc.setTextColor(...inkMuted);
  doc.text(`Generated ${nowStr}  ·  PM Poshan MDM Calculator  ·  One-page compact statement`, 14, 292);
  doc.text("Page 1 of 1", 196, 292, { align: "right" });
  doc.setFillColor(...accent); doc.rect(0, 295, 210, 1.5, "F");
  doc.setFillColor(...primary); doc.rect(0, 296.5, 210, 0.5, "F");

  const filename = `PM_POSHAN_COMPACT_${result.date.toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
}

// ------------------------------------------------------------------
// POSHAN PANJI monthly register — one PDF summarising all saved entries
// ------------------------------------------------------------------
export async function exportPanjiPDF({ entries, school, from_date, to_date, monthLabel }) {
  const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
  await loadCarlito(doc);

  const primary = [4, 120, 87];
  const accent = [234, 88, 12];
  const inkDark = [15, 23, 42];
  const inkMuted = [71, 85, 105];
  const line = [203, 213, 225];
  const softBg = [248, 250, 252];

  const nowStr = new Date().toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  const schoolName = (school?.name || "").trim();
  const udise = (school?.udise || "").trim();

  drawLetterhead(doc, {
    title: schoolName ? schoolName.toUpperCase() : "POSHAN PANJI REGISTER",
    sub1: `UDISE Code: ${udise || "—"}   ·   PM POSHAN Mid-Day Meal Programme`,
    sub2: `Poshan Panji Register — ${monthLabel}`,
    refNo: `PANJI/${udise || "XXX"}/${from_date.replace(/-/g, "")}`,
    dated: `${from_date} → ${to_date}`,
    ayear: `Entries: ${entries.length}`,
    primary, accent, inkDark, inkMuted, line,
  });

  // Aggregate
  let totalStudents = 0, totalCost = 0, totalRice = 0;
  entries.forEach((e) => {
    totalStudents += Number(e.summary?.total_students || 0);
    totalCost += Number(e.summary?.grand_total || 0);
    totalRice += Number(e.summary?.total_rice_kg || 0);
  });

  let y = 42;
  const kcards = [
    { l: "Days Recorded", v: String(entries.length) },
    { l: "Total Meals Served", v: String(totalStudents) },
    { l: "Total Cost (₹)", v: money(totalCost) },
    { l: "Total Rice", v: `${totalRice.toFixed(2)} kg` },
  ];
  kcards.forEach((c, i) => {
    const x = 14 + i * 45.5;
    doc.setFillColor(...softBg); doc.setDrawColor(...line);
    doc.roundedRect(x, y, 42.5, 15, 2, 2, "FD");
    doc.setFont("Carlito", "normal"); doc.setFontSize(7); doc.setTextColor(...inkMuted);
    doc.text(c.l.toUpperCase(), x + 3, y + 5);
    doc.setFont("Carlito", "bold"); doc.setFontSize(11); doc.setTextColor(...inkDark);
    doc.text(c.v, x + 3, y + 12);
  });

  y = y + 20;
  sectionHeading(doc, "Day-wise Poshan Panji", 14, y, primary);
  const rows = entries.map((e) => [
    e.date_iso,
    e.weekday_name || "",
    (e.entry_type || "meal") === "ladoo" ? "🧁 Ladoo" : "🍛 Meal",
    stripEmoji(e.menu_display || ""),
    String(e.counts?.balvatika ?? 0),
    String(e.counts?.primary ?? 0),
    String(e.counts?.upper_primary ?? 0),
    String(e.counts?.high_school ?? 0),
    String(e.summary?.total_students ?? 0),
    (Number(e.summary?.total_rice_kg || 0)).toFixed(2),
    money(e.summary?.grand_total || 0),
  ]);
  autoTable(doc, {
    head: [["Date", "Day", "Type", "Menu", "Bal", "Pri", "UP", "HS", "Total", "Rice (kg)", "Cost (₹)"]],
    body: rows.length ? rows : [["—", "", "", "No entries in this range", "", "", "", "", "", "", ""]],
    foot: [["", "", "", "Total", "", "", "", "", String(totalStudents), totalRice.toFixed(2), money(totalCost)]],
    startY: y + 4,
    theme: "grid",
    styles: { font: "Carlito", fontSize: 8, cellPadding: 1.2, textColor: inkDark, lineColor: line, lineWidth: 0.12 },
    headStyles: { fillColor: primary, textColor: 255, fontStyle: "bold", fontSize: 8 },
    footStyles: { fillColor: [236, 253, 245], textColor: primary, fontStyle: "bold", fontSize: 8 },
    columnStyles: {
      4: { halign: "center" }, 5: { halign: "center" }, 6: { halign: "center" }, 7: { halign: "center" },
      8: { halign: "center" }, 9: { halign: "right" }, 10: { halign: "right" },
    },
    alternateRowStyles: { fillColor: [252, 252, 252] },
    margin: { left: 14, right: 14 },
  });

  // Signature strip on the last page
  let sy = doc.lastAutoTable.finalY + 10;
  if (sy > 258) { doc.addPage(); sy = 30; drawLetterhead(doc, { title: schoolName ? schoolName.toUpperCase() : "POSHAN PANJI REGISTER", sub1: `UDISE Code: ${udise || "—"}`, sub2: `Poshan Panji — ${monthLabel} (continued)`, refNo: "—", dated: `${from_date} → ${to_date}`, ayear: "", primary, accent, inkDark, inkMuted, line }); sy = 44; }
  doc.setFont("Carlito", "italic"); doc.setFontSize(8); doc.setTextColor(...inkMuted);
  doc.text("Certified that the entries above reflect meals served and cost incurred under the PM POSHAN scheme for the period shown.", 14, sy, { maxWidth: 182 });
  const sigY = sy + 14;
  doc.setDrawColor(...inkMuted); doc.setLineWidth(0.3);
  ["Meal In-charge", "Head Master / Principal", "Inspecting In-charge"].forEach((l, i) => {
    const px = 18 + i * 62;
    doc.line(px, sigY, px + 50, sigY);
    doc.setFont("Carlito", "bold"); doc.setFontSize(8.5); doc.setTextColor(...inkDark);
    doc.text(l, px + 25, sigY + 4, { align: "center" });
  });

  // Page numbers + footer (no contact info)
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setDrawColor(...line); doc.setLineWidth(0.2);
    doc.line(14, 286, 196, 286);
    doc.setFont("Carlito", "normal"); doc.setFontSize(7.5); doc.setTextColor(...inkMuted);
    doc.text(`Generated ${nowStr}  ·  Poshan Panji Register  ·  PM Poshan MDM Calculator`, 14, 292);
    doc.text(`Page ${i} of ${totalPages}`, 196, 292, { align: "right" });
    doc.setFillColor(...accent); doc.rect(0, 295, 210, 1.5, "F");
    doc.setFillColor(...primary); doc.rect(0, 296.5, 210, 0.5, "F");
  }

  const filename = `POSHAN_PANJI_${udise || "SCHOOL"}_${from_date}_to_${to_date}.pdf`;
  doc.save(filename);
}

// ------------------------------------------------------------------
// LADDOO ONE-PAGER PDF — formal single-A4 statement for the /laddoo page
// ------------------------------------------------------------------
export async function exportLaddooPDF({ date, students, recipe, school, academicYear, weekdayName }) {
  const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
  await loadCarlito(doc);

  const primary = [4, 120, 87];
  const accent = [234, 88, 12];
  const inkDark = [15, 23, 42];
  const inkMuted = [71, 85, 105];
  const line = [203, 213, 225];
  const softBg = [248, 250, 252];

  const dateStr = date.toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });
  const nowStr = new Date().toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  const schoolName = (school?.name || "").trim();
  const udise = (school?.udise || "").trim();
  const perLadoo = recipe.reduce((s, i) => s + Number(i.rate_per_ladoo || 0), 0);
  const grams = recipe.reduce((s, i) => s + (i.is_service ? 0 : Number(i.grams_per_ladoo || 0)), 0);
  const makingCharge = recipe.find((i) => i.is_service)?.rate_per_ladoo || 0;
  const totalBudget = students * perLadoo;
  const totalMaking = students * makingCharge;

  drawLetterhead(doc, {
    title: schoolName ? schoolName.toUpperCase() : "NUTRITION LADOO STATEMENT",
    sub1: schoolName ? `UDISE Code: ${udise || "—"}   ·   PM POSHAN Mid-Day Meal Programme` : "Ladoo Service Day Statement",
    sub2: `🧁 Nutrition Ladoo Service Day  ·  ${students} laddoos`,
    refNo: `PPC-LDU/${academicYear}/${String(date.getTime()).slice(-6)}`,
    dated: dateStr,
    ayear: `${weekdayName}   ·   AY ${academicYear}`,
    primary, accent, inkDark, inkMuted, line,
  });

  // KPI strip
  let y = 42;
  const cards = [
    { l: "Students", v: String(students) },
    { l: "Ladoos", v: String(students) },
    { l: "Per-Ladoo (₹)", v: money(perLadoo) },
    { l: "Total Budget (₹)", v: money(totalBudget) },
    { l: "Making Charges (₹)", v: money(totalMaking) },
  ];
  const cw = 34.4;
  cards.forEach((c, i) => {
    const x = 14 + i * (cw + 2);
    doc.setFillColor(...softBg); doc.setDrawColor(...line);
    doc.roundedRect(x, y, cw, 13, 2, 2, "FD");
    doc.setFont("Carlito", "normal"); doc.setFontSize(6.5); doc.setTextColor(...inkMuted);
    doc.text(c.l.toUpperCase(), x + 2.5, y + 4.5);
    doc.setFont("Carlito", "bold"); doc.setFontSize(11); doc.setTextColor(...inkDark);
    doc.text(c.v, x + 2.5, y + 10.5);
  });

  // Per-Ladoo recipe
  y = y + 18;
  sectionHeading(doc, "Per-Ladoo Recipe (as per Odisha norm)", 14, y, primary);
  const perRows = recipe.map((it) => [
    `${it.emoji || ""}  ${stripEmoji(it.name)}`.trim(),
    it.is_service ? "—" : `${Number(it.grams_per_ladoo || 0).toFixed(2)} g`,
    money(it.rate_per_ladoo || 0),
  ]);
  autoTable(doc, {
    head: [["Ingredient", "Grams / Ladoo", "Rate (₹)"]],
    body: perRows,
    foot: [["Total per Ladoo", `${grams.toFixed(2)} g`, money(perLadoo)]],
    startY: y + 4,
    theme: "grid",
    styles: { font: "Carlito", fontSize: 8.5, cellPadding: 1.2, textColor: inkDark, lineColor: line, lineWidth: 0.12 },
    headStyles: { fillColor: primary, textColor: 255, fontStyle: "bold", fontSize: 8.5 },
    footStyles: { fillColor: [236, 253, 245], textColor: primary, fontStyle: "bold", fontSize: 8.5 },
    columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
    alternateRowStyles: { fillColor: [252, 252, 252] },
    margin: { left: 14, right: 108 },
    tableWidth: 88,
  });
  const leftEnd = doc.lastAutoTable.finalY;

  // Single-day totals on the right side (parallel table)
  const totalsRows = recipe.map((it) => {
    const g = Number(it.grams_per_ladoo || 0) * students;
    const r = Number(it.rate_per_ladoo || 0) * students;
    return [
      `${it.emoji || ""}  ${stripEmoji(it.name)}`.trim(),
      it.is_service ? "—" : (g >= 1000 ? `${(g / 1000).toFixed(3)} kg` : `${g.toFixed(2)} g`),
      money(r),
    ];
  });
  autoTable(doc, {
    head: [["Ingredient", `Total (× ${students})`, "Cost (₹)"]],
    body: totalsRows,
    foot: [["Grand Total", `${((grams * students) / 1000).toFixed(3)} kg`, money(totalBudget)]],
    startY: y + 4,
    theme: "grid",
    styles: { font: "Carlito", fontSize: 8.5, cellPadding: 1.2, textColor: inkDark, lineColor: line, lineWidth: 0.12 },
    headStyles: { fillColor: accent, textColor: 255, fontStyle: "bold", fontSize: 8.5 },
    footStyles: { fillColor: [255, 247, 237], textColor: accent, fontStyle: "bold", fontSize: 8.5 },
    columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
    alternateRowStyles: { fillColor: [252, 252, 252] },
    margin: { left: 108, right: 14 },
    tableWidth: 88,
  });
  const rightEnd = doc.lastAutoTable.finalY;

  // Certification & signature
  let sy = Math.max(leftEnd, rightEnd) + 8;
  if (sy > 258) sy = 258;
  doc.setFont("Carlito", "italic"); doc.setFontSize(7.5); doc.setTextColor(...inkMuted);
  doc.text(
    `Certified that ${students} nutrition laddoos were prepared and served on the date above, in accordance with the per-child norm of ₹${perLadoo.toFixed(2)} (statutory cap ₹3.50).`,
    14, sy, { maxWidth: 182 }
  );
  const sigY = sy + 14;
  doc.setDrawColor(...inkMuted); doc.setLineWidth(0.3);
  ["Meal In-charge", "Head Master / Principal", "Inspecting In-charge"].forEach((l, i) => {
    const px = 18 + i * 62;
    doc.line(px, sigY, px + 50, sigY);
    doc.setFont("Carlito", "bold"); doc.setFontSize(8); doc.setTextColor(...inkDark);
    doc.text(l, px + 25, sigY + 4, { align: "center" });
    doc.setFont("Carlito", "italic"); doc.setFontSize(6.5); doc.setTextColor(...inkMuted);
    doc.text("(Signature & date)", px + 25, sigY + 7.5, { align: "center" });
  });

  // Footer (no contact info)
  doc.setDrawColor(...line); doc.setLineWidth(0.2);
  doc.line(14, 286, 196, 286);
  doc.setFont("Carlito", "normal"); doc.setFontSize(7); doc.setTextColor(...inkMuted);
  doc.text(`Generated ${nowStr}  ·  PM Poshan MDM Calculator  ·  Ladoo service day statement`, 14, 292);
  doc.text("Page 1 of 1", 196, 292, { align: "right" });
  doc.setFillColor(...accent); doc.rect(0, 295, 210, 1.5, "F");
  doc.setFillColor(...primary); doc.rect(0, 296.5, 210, 0.5, "F");

  const filename = `PM_POSHAN_LADOO_${date.toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
}

// Shared letterhead used by both single-day and bulk PDFs
function drawLetterhead(doc, o) {
  const { title, sub1, sub2, refNo, dated, ayear, primary, accent, inkDark, inkMuted, line } = o;

  doc.setFillColor(...primary); doc.rect(0, 0, 210, 8, "F");
  doc.setFillColor(...accent); doc.rect(0, 8, 210, 1.5, "F");

  doc.setFillColor(...primary);
  doc.roundedRect(14, 14, 18, 18, 3, 3, "F");
  doc.setFont("Carlito", "bold"); doc.setFontSize(13); doc.setTextColor(255, 255, 255);
  doc.text("PMC", 23, 25.5, { align: "center" });

  doc.setTextColor(...inkDark);
  doc.setFont("Carlito", "bold"); doc.setFontSize(15);
  doc.text(title, 37, 20, { maxWidth: 130 });
  doc.setFont("Carlito", "normal"); doc.setFontSize(9.5); doc.setTextColor(...inkMuted);
  doc.text(sub1, 37, 25.5, { maxWidth: 160 });
  doc.text(sub2, 37, 30, { maxWidth: 160 });

  doc.setFontSize(8.5); doc.setTextColor(...inkMuted);
  doc.text(`Ref. No.: ${refNo}`, 196, 18, { align: "right" });
  doc.text(`Dated: ${dated}`, 196, 22.5, { align: "right" });
  doc.text(ayear, 196, 27, { align: "right" });

  doc.setDrawColor(...line); doc.setLineWidth(0.3);
  doc.line(14, 36, 196, 36);
}
